import React from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Paper, 
  Divider, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText, 
  Grid,
  useTheme
} from '@mui/material';
import { 
  Check, 
  Info, 
  Settings, 
  PowerSettingsNew, 
  NetworkCheck, 
  WarningAmber 
} from '@mui/icons-material';
import setsyncBoxImage from '../../assets/setsync_box.png';

const RetailPlayerSupportPage = () => {
  const theme = useTheme();

  return (
    <Container maxWidth="md" sx={{ pt: 4, pb: 8 }}>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
        <Typography variant="h4" component="h1" gutterBottom color="primary" fontWeight="bold">
          RetailPlayer M400 Support Guide
        </Typography>
        
        <Typography variant="subtitle1" paragraph color="text.secondary">
          This guide provides essential information for setting up and troubleshooting your RetailPlayer M400 device.
        </Typography>
        
        {/* SetSync Box Image */}
        <Box 
          sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            my: 4,
            p: 3,
            backgroundColor: 'rgba(255, 107, 26, 0.05)',
            borderRadius: 2,
            border: '1px solid rgba(255, 107, 26, 0.2)',
          }}
        >
          <Box
            component="img"
            src={setsyncBoxImage}
            alt="SetSync Box - RetailPlayer M400"
            sx={{
              width: '100%',
              maxWidth: 400,
              height: 'auto',
              filter: 'drop-shadow(0 10px 30px rgba(0, 0, 0, 0.3))',
            }}
          />
        </Box>
        
        <Divider sx={{ my: 3 }} />
        
        <Typography variant="h5" component="h2" gutterBottom color="primary">
          Package Contents
        </Typography>
        
        <List>
          {['M400 Device', 'Audio Cable with RCA Connectors', 'Power Supply Unit', 'Quick Install Guide'].map((item, index) => (
            <ListItem key={index} sx={{ py: 0.5 }}>
              <ListItemIcon>
                <Check color="primary" />
              </ListItemIcon>
              <ListItemText primary={item} />
            </ListItem>
          ))}
        </List>
        
        <Divider sx={{ my: 3 }} />
        
        <Typography variant="h5" component="h2" gutterBottom color="primary">
          Installation Steps
        </Typography>
        
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Settings color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6" component="h3">
              Step 1: Connect the Audio Equipment
            </Typography>
          </Box>
          <Typography variant="body1" sx={{ pl: 4 }}>
            Connect the M400's unbalanced line-level output to your audio system using the provided RCA connectors.
          </Typography>
        </Box>
        
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <NetworkCheck color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6" component="h3">
              Step 2: Network Connection
            </Typography>
          </Box>
          <Typography variant="body1" sx={{ pl: 4 }}>
            Use a standard Ethernet cable (CAT5e or better) to connect the M400 to your network.
          </Typography>
        </Box>
        
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <PowerSettingsNew color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6" component="h3">
              Step 3: Power Connection
            </Typography>
          </Box>
          <Typography variant="body1" sx={{ pl: 4 }}>
            Plug the power supply into the M400. The device will boot up, and the status LEDs will indicate the progress:
          </Typography>
          <List sx={{ pl: 4 }}>
            <ListItem sx={{ py: 0.5 }}>
              <ListItemText primary="Both LEDs red during boot." />
            </ListItem>
            <ListItem sx={{ py: 0.5 }}>
              <ListItemText primary="Left LED turns green when the device announces its IP address." />
            </ListItem>
            <ListItem sx={{ py: 0.5 }}>
              <ListItemText primary="Right LED turns green when the application is ready." />
            </ListItem>
          </List>
        </Box>
        
        <Divider sx={{ my: 3 }} />
        
        <Typography variant="h5" component="h2" gutterBottom color="primary">
          LED Status Indicators
        </Typography>
        
        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6}>
            <Paper elevation={1} sx={{ p: 2, height: '100%', backgroundColor: theme.palette.background.default }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                System Booting
              </Typography>
              <Typography variant="body2">
                Left LED red, Right LED off
              </Typography>
            </Paper>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <Paper elevation={1} sx={{ p: 2, height: '100%', backgroundColor: theme.palette.background.default }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                System Ready
              </Typography>
              <Typography variant="body2">
                Both LEDs green
              </Typography>
            </Paper>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <Paper elevation={1} sx={{ p: 2, height: '100%', backgroundColor: theme.palette.background.default }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Application Running
              </Typography>
              <Typography variant="body2">
                Both LEDs green, indicating a connection to the RetailPlayer Portal
              </Typography>
            </Paper>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <Paper elevation={1} sx={{ p: 2, height: '100%', backgroundColor: theme.palette.background.default }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Firmware Update
              </Typography>
              <Typography variant="body2">
                Left LED orange, Right LED green
              </Typography>
            </Paper>
          </Grid>
        </Grid>
        
        <Divider sx={{ my: 3 }} />
        
        <Typography variant="h5" component="h2" gutterBottom color="primary">
          Troubleshooting
        </Typography>
        
        <List>
          <ListItem alignItems="flex-start">
            <ListItemIcon>
              <WarningAmber color="warning" />
            </ListItemIcon>
            <ListItemText 
              primary="Power Issues" 
              secondary="If the device does not power on, check the power connection and ensure the power supply is functioning."
            />
          </ListItem>
          
          <ListItem alignItems="flex-start">
            <ListItemIcon>
              <WarningAmber color="warning" />
            </ListItemIcon>
            <ListItemText 
              primary="Network Issues" 
              secondary="For network issues, verify the Ethernet connection and ensure the network settings are correct."
            />
          </ListItem>
          
          <ListItem alignItems="flex-start">
            <ListItemIcon>
              <WarningAmber color="warning" />
            </ListItemIcon>
            <ListItemText 
              primary="Application Not Running" 
              secondary="If the application is not running, check the LED status and refer to the Quick Install Guide for further instructions."
            />
          </ListItem>
        </List>
        
        <Divider sx={{ my: 3 }} />
        
        <Typography variant="h5" component="h2" gutterBottom color="primary">
          Compliance and Safety
        </Typography>
        
        <Typography variant="body1" paragraph>
          The RetailPlayer M400 complies with FCC Class B digital device standards. Ensure proper installation to avoid interference with other electronic devices.
        </Typography>
        
        <Box sx={{ backgroundColor: theme.palette.background.default, p: 3, borderRadius: 2, mt: 3 }}>
          <Typography variant="h6" component="h3" gutterBottom>
            Contact Support
          </Typography>
          
          <Typography variant="body1" paragraph>
            For further assistance, contact Barix support at:
          </Typography>
          
          <Typography variant="body2" component="div" sx={{ mb: 1 }}>
            International: +41 434 33 22 22
          </Typography>
          
          <Typography variant="body2" component="div" sx={{ mb: 1 }}>
            USA: +1 866 815 0866
          </Typography>
          
          <Typography variant="body2" component="div">
            Email: support@barix.com
          </Typography>
        </Box>
        
        <Box sx={{ mt: 3, display: 'flex', alignItems: 'center' }}>
          <Info fontSize="small" color="primary" sx={{ mr: 1 }} />
          <Typography variant="body2" color="text.secondary">
            For more detailed information, refer to the RetailPlayer user manual and safety guidelines available at Barix.com.
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default RetailPlayerSupportPage; 